/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState, Fragment } from "react";
import { Plus, Trash, Warning, ShieldWarning } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import FieldGuide from "@/components/FieldGuide";
import { fmtNum } from "@/lib/format";

const CATEGORIES = [
  { id: "Regulatory", guide: "risk.regulatory" },
  { id: "Supply", guide: "risk.supply" },
  { id: "Price", guide: "risk.price" },
  { id: "Climate", guide: "risk.climate" },
  { id: "ESG", guide: "risk.regulatory" },
];

const LIKE_LABELS = ["Rare", "Unlikely", "Possible", "Likely", "Almost certain"];
const IMP_LABELS = ["Trivial", "Minor", "Moderate", "Major", "Severe"];

function riskColor(score) {
  if (score >= 16) return "bg-risk text-white";
  if (score >= 9) return "bg-warn text-black";
  if (score >= 4) return "bg-sky2/40 theme-ink";
  return "bg-eco/40 theme-ink";
}

export default function Risk() {
  const { active } = useProjects();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "Regulatory", title: "", likelihood: 3, impact: 3, mitigation: "" });

  const load = async () => {
    if (!active) return;
    const { data } = await api.get(`/projects/${active.id}/risks`);
    setItems(data.items);
  };
  useEffect(() => { load(); }, [active]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${active.id}/risks`, form);
      toast.success("Risk added");
      setForm({ ...form, title: "", mitigation: "" });
      await load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const del = async (id) => {
    await api.delete(`/projects/${active.id}/risks/${id}`);
    await load();
  };

  if (!active) return <div className="p-12 theme-ink-muted">Select a project.</div>;

  // 5x5 heatmap data
  const heat = Array.from({ length: 5 }, () => Array(5).fill(0));
  items.forEach((r) => {
    if (r.likelihood >= 1 && r.likelihood <= 5 && r.impact >= 1 && r.impact <= 5) {
      heat[5 - r.impact][r.likelihood - 1] += 1;
    }
  });

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Climate & Carbon Risk"
        title="Risk register."
        subtitle="Map regulatory, supply-chain, price, climate and ESG risks. The 5×5 heatmap feeds the Carbon Risk Index (CRI)."
        guideId="risk"
      />

      <section className="px-8 pt-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <form onSubmit={add} className="border theme-border theme-surface p-6 space-y-3" data-testid="risk-form">
          <div className="overline">New risk</div>
          <h3 className="font-serif-display text-xl theme-ink">Add</h3>

          <div>
            <label className="overline mb-1.5 block">Category</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold">
              {CATEGORIES.map((c) => <option key={c.id}>{c.id}</option>)}
            </select>
          </div>

          <div>
            <label className="overline flex items-center gap-1 mb-1.5">Title <FieldGuide field={CATEGORIES.find((c) => c.id === form.category)?.guide || "risk.regulatory"} /></label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="risk-title-input"
              className="w-full theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              placeholder="e.g. CBAM scope expansion 2027" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline mb-1.5 block">Likelihood</label>
              <select value={form.likelihood} onChange={(e) => setForm({ ...form, likelihood: Number(e.target.value) })}
                className="w-full theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold">
                {LIKE_LABELS.map((l, i) => <option key={l} value={i + 1}>{i + 1} · {l}</option>)}
              </select>
            </div>
            <div>
              <label className="overline mb-1.5 block">Impact</label>
              <select value={form.impact} onChange={(e) => setForm({ ...form, impact: Number(e.target.value) })}
                className="w-full theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold">
                {IMP_LABELS.map((l, i) => <option key={l} value={i + 1}>{i + 1} · {l}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="overline mb-1.5 block">Mitigation</label>
            <textarea value={form.mitigation} onChange={(e) => setForm({ ...form, mitigation: e.target.value })} rows={2}
              className="w-full theme-input border px-3 py-2 text-sm rounded-sm focus:outline-none focus:border-gold"
              placeholder="Specify low-carbon EPDs · diversify suppliers · lock-in fixed price contracts…" />
          </div>

          <button type="submit" data-testid="risk-add-btn"
            className="w-full bg-gold text-black font-medium py-2.5 rounded-sm hover:bg-[#e0bc40] flex items-center justify-center gap-2">
            <Plus size={16} weight="bold" /> Add risk
          </button>
        </form>

        <div className="xl:col-span-2 space-y-4">
          <div className="border theme-border theme-surface p-6">
            <div className="overline">5 × 5 heatmap</div>
            <div className="font-serif-display text-2xl theme-ink mt-1">Impact × likelihood</div>
            <div className="mt-4 grid grid-cols-[60px_repeat(5,1fr)] gap-1 text-[10px] font-mono-data">
              <div />
              {[1, 2, 3, 4, 5].map((l) => (
                <div key={l} className="text-center theme-ink-dim uppercase tracking-widest">L{l}</div>
              ))}
              {heat.map((row, ri) => (
                <Fragment key={`row-${ri}`}>
                  <div className="text-right theme-ink-dim uppercase tracking-widest pr-1">I{5 - ri}</div>
                  {row.map((cell, ci) => {
                    const score = (5 - ri) * (ci + 1);
                    return (
                      <div key={`${ri}-${ci}`} className={`aspect-square flex items-center justify-center ${riskColor(score)}`}>
                        <span className="font-mono-data text-sm">{cell || ""}</span>
                      </div>
                    );
                  })}
                </Fragment>
              ))}
            </div>
          </div>

          <div className="border theme-border theme-surface overflow-hidden">
            <div className="px-6 py-4 border-b theme-border">
              <div className="overline">Risk register · {items.length} items</div>
              <div className="font-serif-display text-xl theme-ink">Open exposures</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                  <tr>
                    <th className="text-left px-6 py-3">Title</th>
                    <th className="text-left px-3 py-3">Category</th>
                    <th className="text-right px-3 py-3">L</th>
                    <th className="text-right px-3 py-3">I</th>
                    <th className="text-right px-3 py-3">Score</th>
                    <th className="text-left px-3 py-3">Mitigation</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-t theme-border">
                      <td className="px-6 py-3 theme-ink">{r.title}</td>
                      <td className="px-3 theme-ink-muted">{r.category}</td>
                      <td className="px-3 py-3 text-right font-mono-data">{r.likelihood}</td>
                      <td className="px-3 py-3 text-right font-mono-data">{r.impact}</td>
                      <td className="px-3 py-3 text-right">
                        <span className={`inline-flex items-center justify-center w-8 h-6 ${riskColor(r.score)}`}>{r.score}</span>
                      </td>
                      <td className="px-3 theme-ink-muted text-xs">{r.mitigation || "—"}</td>
                      <td className="px-3 py-3 text-right">
                        <button onClick={() => del(r.id)} className="theme-ink-dim hover:text-risk"><Trash size={14} /></button>
                      </td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr><td colSpan={7} className="px-6 py-10 text-center theme-ink-dim">No risks logged.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
      <div className="pb-12" />
    </div>
  );
}
